import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './BookLesson.css';

const BookLesson = ({ teacher, teacherSchedule, onClose }) => {
  const { user } = useAuth();
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState({}); // {dayOfWeek: hour}
  const [lessonsPerWeek, setLessonsPerWeek] = useState(1);
  const [courseSize, setCourseSize] = useState(1); // 1, 5 или 30 уроков
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const [availableHoursByDay, setAvailableHoursByDay] = useState({}); // {dayOfWeek: [hours]}
  const [bookedLessons, setBookedLessons] = useState([]);
  const [blockedHours, setBlockedHours] = useState([]);

  const dayNames = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

  useEffect(() => {
    // Загружаем забронированные уроки и заблокированные часы
    if (teacher) {
      fetchBookedLessons();
      fetchBlockedHours();
    }
  }, [teacher]);
  
  useEffect(() => {
    console.log('[BookLesson] teacherSchedule изменился:', teacherSchedule);
    console.log('[BookLesson] teacherSchedule тип:', typeof teacherSchedule, Array.isArray(teacherSchedule));
    console.log('[BookLesson] teacherSchedule длина:', teacherSchedule?.length);
  }, [teacherSchedule]);

  useEffect(() => {
    // Определяем доступные дни и часы на основе расписания учителя, исключая занятые
    const days = [];
    const hoursByDay = {};
    
    // Проверяем, что teacherSchedule существует и является массивом
    if (!teacherSchedule || !Array.isArray(teacherSchedule)) {
      console.log('[BookLesson] teacherSchedule не определен или не массив:', teacherSchedule);
      setAvailableDays([]);
      setAvailableHoursByDay({});
      return;
    }
    
    if (teacherSchedule.length === 0) {
      console.log('[BookLesson] teacherSchedule пустой массив');
      setAvailableDays([]);
      setAvailableHoursByDay({});
      return;
    }
    
    console.log('[BookLesson] teacherSchedule:', teacherSchedule);
    console.log('[BookLesson] bookedLessons:', bookedLessons);
    console.log('[BookLesson] blockedHours:', blockedHours);
    
    for (let day = 0; day < 7; day++) {
      // Базовые доступные часы из расписания
      const baseHours = teacherSchedule
        .filter(slot => slot.day_of_week === day && slot.is_available)
        .map(slot => slot.hour);
      
      console.log(`[BookLesson] День ${day}, базовые часы:`, baseHours);
      
      // Вычисляем ближайшую дату для этого дня недели
      const nextDate = getNextDateForDay(day);
      const dateStr = nextDate.toISOString().split('T')[0];
      
      // Исключаем заблокированные часы
      let availableHours = [...baseHours];
      
      // Убираем повторяющиеся блокировки для этого дня недели
      const recurringBlocks = blockedHours.filter(
        block => block.is_recurring && block.day_of_week === day
      );
      const blockedHoursRecurring = recurringBlocks.map(block => block.hour);
      console.log(`[BookLesson] День ${day}, повторяющиеся блокировки:`, blockedHoursRecurring);
      availableHours = availableHours.filter(h => !blockedHoursRecurring.includes(h));
      
      // Убираем одноразовые блокировки на эту дату
      const oneTimeBlocks = blockedHours.filter(
        block => !block.is_recurring && block.date === dateStr
      );
      const blockedHoursOneTime = oneTimeBlocks.map(block => block.hour);
      console.log(`[BookLesson] День ${day}, одноразовые блокировки на ${dateStr}:`, blockedHoursOneTime);
      availableHours = availableHours.filter(h => !blockedHoursOneTime.includes(h));
      
      // Убираем уже забронированные уроки на эту дату
      const bookedOnDate = bookedLessons.filter(lesson => {
        const lessonDate = new Date(lesson.scheduled_at);
        const lessonDateStr = lessonDate.toISOString().split('T')[0];
        return lessonDateStr === dateStr && 
               (lesson.status === 'scheduled' || lesson.status === 'in_progress');
      });
      const bookedHours = bookedOnDate.map(lesson => {
        const lessonDate = new Date(lesson.scheduled_at);
        return lessonDate.getUTCHours();
      });
      console.log(`[BookLesson] День ${day}, забронированные часы на ${dateStr}:`, bookedHours);
      availableHours = availableHours.filter(h => !bookedHours.includes(h));
      
      // Сортируем и убираем дубликаты
      availableHours = [...new Set(availableHours)].sort((a, b) => a - b);
      console.log(`[BookLesson] День ${day}, итоговые доступные часы:`, availableHours);
      
      if (availableHours.length > 0) {
        days.push(day);
        hoursByDay[day] = availableHours;
      }
    }
    
    console.log('[BookLesson] Итоговые доступные дни:', days);
    console.log('[BookLesson] Итоговые доступные часы по дням:', hoursByDay);
    
    setAvailableDays(days);
    setAvailableHoursByDay(hoursByDay);
  }, [teacherSchedule, bookedLessons, blockedHours]);

  const getNextDateForDay = (dayOfWeek) => {
    const today = new Date();
    const jsDayOfWeek = dayOfWeek === 6 ? 0 : dayOfWeek + 1;
    const currentDay = today.getDay();
    let daysUntilDay = (jsDayOfWeek - currentDay + 7) % 7;
    if (daysUntilDay === 0) daysUntilDay = 7;
    
    const targetDate = new Date(today);
    targetDate.setUTCDate(today.getUTCDate() + daysUntilDay);
    targetDate.setUTCHours(0, 0, 0, 0);
    return targetDate;
  };

  const fetchBookedLessons = async () => {
    try {
      // Получаем все уроки учителя и фильтруем на клиенте
      const response = await api.get('/lessons/lessons/');
      const allLessons = response.data.results || response.data || [];
      // Фильтруем уроки этого учителя со статусом scheduled или in_progress
      const booked = allLessons.filter(lesson => 
        lesson.teacher === teacher.id && 
        (lesson.status === 'scheduled' || lesson.status === 'in_progress')
      );
      setBookedLessons(booked);
    } catch (error) {
      console.error('Ошибка загрузки забронированных уроков:', error);
      setBookedLessons([]);
    }
  };

  const fetchBlockedHours = async () => {
    try {
      // Заблокированные часы доступны только для учителя, но мы можем попробовать получить их
      // Если нет доступа, просто вернем пустой массив
      const response = await api.get(`/lessons/blocked-hours/`, {
        params: {
          teacher: teacher.id
        }
      }).catch(() => ({ data: [] }));
      setBlockedHours(response.data.results || response.data || []);
    } catch (error) {
      console.error('Ошибка загрузки заблокированных часов:', error);
      setBlockedHours([]);
    }
  };

  const toggleDay = (day) => {
    if (!availableDays.includes(day)) return;
    
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        // Удаляем день и его выбранное время
        setSelectedTimes(prevTimes => {
          const newTimes = { ...prevTimes };
          delete newTimes[day];
          return newTimes;
        });
        return prev.filter(d => d !== day);
      } else {
        if (prev.length < lessonsPerWeek) {
          // При добавлении дня выбираем первое доступное время по умолчанию
          const hours = availableHoursByDay[day] || [];
          if (hours.length > 0) {
            setSelectedTimes(prevTimes => ({
              ...prevTimes,
              [day]: hours[0]
            }));
          }
          return [...prev, day];
        }
        return prev;
      }
    });
  };

  const handleTimeChange = (day, hour) => {
    setSelectedTimes(prev => ({
      ...prev,
      [day]: hour
    }));
  };

  const handleLessonsPerWeekChange = (value) => {
    setLessonsPerWeek(value);
    // Ограничиваем выбранные дни до нового значения
    if (selectedDays.length > value) {
      const daysToRemove = selectedDays.slice(value);
      setSelectedDays(selectedDays.slice(0, value));
      // Удаляем времена для удаленных дней
      setSelectedTimes(prevTimes => {
        const newTimes = { ...prevTimes };
        daysToRemove.forEach(day => delete newTimes[day]);
        return newTimes;
      });
    }
  };

  const getNextAvailableDate = (dayOfWeek, hour) => {
    // Находим ближайшую доступную дату для выбранного дня недели и часа
    // Проверяем, не занят ли этот слот
    let targetDate = getNextDateForDay(dayOfWeek);
    targetDate.setUTCHours(hour, 0, 0, 0);
    
    // Проверяем, не занят ли этот слот (максимум на 4 недели вперед)
    for (let weekOffset = 0; weekOffset < 4; weekOffset++) {
      const checkDate = new Date(targetDate);
      checkDate.setUTCDate(targetDate.getUTCDate() + (weekOffset * 7));
      const dateStr = checkDate.toISOString().split('T')[0];
      
      // Проверяем, не заблокирован ли этот час
      const isBlocked = blockedHours.some(block => {
        if (block.is_recurring && block.day_of_week === dayOfWeek && block.hour === hour) {
          return true;
        }
        if (!block.is_recurring && block.date === dateStr && block.hour === hour) {
          return true;
        }
        return false;
      });
      
      if (isBlocked) continue;
      
      // Проверяем, не забронирован ли уже урок на это время
      const isBooked = bookedLessons.some(lesson => {
        const lessonDate = new Date(lesson.scheduled_at);
        const lessonDateStr = lessonDate.toISOString().split('T')[0];
        const lessonHour = lessonDate.getUTCHours();
        return lessonDateStr === dateStr && 
               lessonHour === hour &&
               (lesson.status === 'scheduled' || lesson.status === 'in_progress');
      });
      
      if (!isBooked) {
        return checkDate;
      }
    }
    
    // Если не нашли свободный слот, возвращаем первую возможную дату
    return targetDate;
  };

  const handleBook = async () => {
    if (selectedDays.length === 0) {
      setError('Выберите хотя бы один день');
      return;
    }

    if (selectedDays.length !== lessonsPerWeek) {
      setError(`Выберите ровно ${lessonsPerWeek} ${lessonsPerWeek === 1 ? 'день' : 'дня'}`);
      return;
    }

    // Проверяем, что для каждого дня выбрано время
    for (const dayOfWeek of selectedDays) {
      if (selectedTimes[dayOfWeek] === undefined) {
        setError(`Выберите время для ${dayNames[dayOfWeek]}`);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Создаем все уроки на основе выбранного курса
      const lessons = [];
      let lessonsCreated = 0;
      let currentWeekOffset = 0;
      let dayIndex = 0;
      const updatedBookedLessons = [...bookedLessons];
      
      while (lessonsCreated < courseSize) {
        // Берем день из выбранных дней по кругу
        const dayOfWeek = selectedDays[dayIndex % selectedDays.length];
        const hour = selectedTimes[dayOfWeek];
        
        // Вычисляем дату для этого дня недели с учетом недельного смещения
        const baseDate = getNextDateForDay(dayOfWeek);
        const targetDate = new Date(baseDate);
        targetDate.setUTCDate(baseDate.getUTCDate() + (currentWeekOffset * 7));
        targetDate.setUTCHours(hour, 0, 0, 0);
        
        // Проверяем, что дата в будущем
        if (targetDate <= new Date()) {
          dayIndex++;
          if (dayIndex % selectedDays.length === 0) {
            currentWeekOffset++;
          }
          continue;
        }
        
        // Проверяем, не занят ли этот слот
        const dateStr = targetDate.toISOString().split('T')[0];
        const isBlocked = blockedHours.some(block => {
          if (block.is_recurring && block.day_of_week === dayOfWeek && block.hour === hour) {
            return true;
          }
          if (!block.is_recurring && block.date === dateStr && block.hour === hour) {
            return true;
          }
          return false;
        });
        
        const isBooked = updatedBookedLessons.some(lesson => {
          const lessonDate = new Date(lesson.scheduled_at);
          const lessonDateStr = lessonDate.toISOString().split('T')[0];
          const lessonHour = lessonDate.getUTCHours();
          return lessonDateStr === dateStr && 
                 lessonHour === hour &&
                 (lesson.status === 'scheduled' || lesson.status === 'in_progress');
        });
        
        // Если слот свободен, создаем урок
        if (!isBlocked && !isBooked) {
          const lessonData = {
            teacher: teacher.id,
            student: user.id,
            scheduled_at: targetDate.toISOString(),
            duration_minutes: 60,
            status: 'scheduled'
          };

          try {
            const response = await api.post('/lessons/lessons/', lessonData);
            lessons.push(response.data);
            updatedBookedLessons.push(response.data);
            lessonsCreated++;
          } catch (lessonError) {
            console.error(`Ошибка создания урока для ${dayNames[dayOfWeek]}:`, lessonError);
            // Продолжаем создание других уроков
          }
        }
        
        // Переходим к следующему дню
        dayIndex++;
        // Если прошли все дни недели, переходим к следующей неделе
        if (dayIndex % selectedDays.length === 0) {
          currentWeekOffset++;
        }
        
        // Защита от бесконечного цикла (максимум 100 недель вперед)
        if (currentWeekOffset > 100) {
          break;
        }
      }
      
      if (lessons.length === 0) {
        setError('Не удалось создать ни одного урока. Возможно, все слоты заняты.');
        setLoading(false);
        return;
      }
      
      alert(`Успешно забронировано ${lessons.length} из ${courseSize} ${courseSize === 1 ? 'урока' : 'уроков'}!`);
      onClose();
      window.location.reload(); // Обновляем страницу для отображения новых уроков
    } catch (error) {
      console.error('Ошибка бронирования урока:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Ошибка при бронировании урока');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="book-lesson-overlay" onClick={onClose}>
      <div className="book-lesson-modal" onClick={(e) => e.stopPropagation()}>
        <div className="book-lesson-header">
          <h2>Забронировать урок</h2>
          <button className="book-lesson-close" onClick={onClose}>×</button>
        </div>

        <div className="book-lesson-content">
          <div className="book-lesson-section">
            <label className="book-lesson-label">
              Какой курс?
            </label>
            <div className="course-size-selector">
              {[1, 5, 30].map(size => (
                <button
                  key={size}
                  className={`course-size-btn ${courseSize === size ? 'active' : ''}`}
                  onClick={() => setCourseSize(size)}
                >
                  {size} {size === 1 ? 'урок' : size < 5 ? 'урока' : 'уроков'}
                </button>
              ))}
            </div>
          </div>

          <div className="book-lesson-section">
            <label className="book-lesson-label">
              Сколько раз в неделю хотите заниматься?
            </label>
            <div className="lessons-per-week-selector">
              {[1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  className={`lessons-per-week-btn ${lessonsPerWeek === num ? 'active' : ''}`}
                  onClick={() => handleLessonsPerWeekChange(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          <div className="book-lesson-section">
            <label className="book-lesson-label">
              Выберите дни недели ({selectedDays.length} из {lessonsPerWeek})
            </label>
            <div className="days-selector">
              {dayNames.map((dayName, index) => {
                const isAvailable = availableDays.includes(index);
                const isSelected = selectedDays.includes(index);
                const isDisabled = !isAvailable || (!isSelected && selectedDays.length >= lessonsPerWeek);

                return (
                  <button
                    key={index}
                    className={`day-btn ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                    onClick={() => toggleDay(index)}
                    disabled={isDisabled}
                  >
                    {dayName}
                  </button>
                );
              })}
            </div>
            {availableDays.length === 0 && (
              <p className="book-lesson-warning">
                У преподавателя нет доступных часов в расписании
              </p>
            )}
          </div>

          {selectedDays.length > 0 && (
            <div className="book-lesson-section">
              <label className="book-lesson-label">
                Выберите время для каждого дня
              </label>
              <div className="time-selector">
                {selectedDays.map(dayOfWeek => {
                  const availableHours = availableHoursByDay[dayOfWeek] || [];
                  const selectedHour = selectedTimes[dayOfWeek];
                  
                  return (
                    <div key={dayOfWeek} className="time-selector-item">
                      <div className="time-selector-day">{dayNames[dayOfWeek]}:</div>
                      <select
                        className="time-selector-select"
                        value={selectedHour !== undefined ? selectedHour : ''}
                        onChange={(e) => handleTimeChange(dayOfWeek, parseInt(e.target.value))}
                      >
                        <option value="">Выберите время</option>
                        {availableHours.map(hour => (
                          <option key={hour} value={hour}>
                            {hour.toString().padStart(2, '0')}:00
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <div className="book-lesson-error">{error}</div>
          )}

          <div className="book-lesson-actions">
            <button
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              className="btn-confirm"
              onClick={handleBook}
              disabled={loading || selectedDays.length !== lessonsPerWeek}
            >
              {loading ? 'Бронирование...' : 'Забронировать'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookLesson;
